import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { filter, take } from 'rxjs';

import { getLandingPagePreference$, getUiPreferencesLoaded$ } from '../modules/taxilla-library/lib/store/selectors/ui-preferences.selector';
import { DeleteUiPreferences, LoadUiPreferences, SaveUiPreferences } from '../modules/taxilla-library/lib/store/actions/ui-preferences.actions';

export interface LandingPageOption {
    id: string;
    name: string;
    icon: string;
    description: string;
    segments: string[];
}

export interface LandingPagePreference {
    /** Page id applied as global fallback when no per-tenant override exists */
    allTenantsPage?: string;
    /** Page id per organization id; takes priority over allTenantsPage */
    tenantPages?: Record<string, string>;
}

export const DEFAULT_LANDING_PAGE_ID = 'transactions';

export const LANDING_PAGE_OPTIONS: LandingPageOption[] = [
    {
        id: 'transactions',
        name: 'Transactions',
        icon: 'receipt_long',
        description: 'Transaction apps and processes',
        segments: ['category', 'transactions'],
    },
    { id: 'tasks', name: 'Tasks', icon: 'checklist', description: 'Track and act on your assigned tasks', segments: ['tasks'] },
    { id: 'dashboard', name: 'Dashboard', icon: 'dashboard', description: 'Analytics and reporting widgets', segments: ['dashboard'] },
    {
        id: 'data-preparation',
        name: 'Data Preparation',
        icon: 'database',
        description: 'Data preparation apps',
        segments: ['category', 'data-preparation'],
    },
    {
        id: 'closures',
        name: 'Closures',
        icon: 'event_available',
        description: 'Closure apps and processes',
        segments: ['category', 'closures'],
    },
    { id: 'returns', name: 'Returns', icon: 'description', description: 'Returns filing apps', segments: ['category', 'returns'] },
    { id: 'utilities', name: 'Utilities', icon: 'dynamic_feed', description: 'Utility apps', segments: ['category', 'utilities'] },
    // Connectors is offered to every tenant for now. It should ultimately be shown only for tenants
    // that actually have connector access, but there is no per-tenant connector-access signal in the
    // client-side data today (the nav item is pushed unconditionally and the org model exposes no such
    // flag). Gate this option once that signal is available.
    { id: 'connectors', name: 'Connectors', icon: 'link', description: 'Manage integrations and connectors', segments: ['connectors'] },
];

const LANDING_PAGE_PREFERENCE_KEY = 'landingPage';

@Injectable({
    providedIn: 'root',
})
export class LandingPagePreferenceService {
    constructor(private store$: Store) {}

    /** Ensures UI preferences are loaded into store from backend before proceeding */
    ensurePreferencesLoaded = (): Promise<void> => {
        return new Promise(resolve => {
            let loaded = false;
            this.store$.select(getUiPreferencesLoaded$).subscribe(isLoaded => {
                loaded = isLoaded;
                if (isLoaded) {
                    resolve();
                }
            }).unsubscribe();
            if (!loaded) {
                this.store$.dispatch(LoadUiPreferences());
                this.store$
                    .select(getUiPreferencesLoaded$)
                    .pipe(
                        filter(l => l === true),
                        take(1)
                    )
                    .subscribe(() => resolve());
            }
        });
    };

    /** Dispatch a backend load of UI preferences (idempotent — effects guard with loaded flag) */
    loadPreference = (): void => {
        this.store$.dispatch(LoadUiPreferences());
    };

    /**
     * Dispatch a save of the landing page preference to the backend.
     * The full preferences object is reconstructed from the current snapshot.
     */
    setPreference = (preference: LandingPagePreference): void => {
        // Read current raw preferences snapshot and merge new landing page value in
        let currentRaw: Record<string, any> = {};
        this.store$.select(state => (state as any)['uiPreferences']?.preferences).subscribe(raw => {
            currentRaw = { ...(raw ?? {}) };
        }).unsubscribe();
        const updated = { ...currentRaw, [LANDING_PAGE_PREFERENCE_KEY]: preference };
        this.store$.dispatch(SaveUiPreferences({ preferences: updated, successMessage: 'Landing page preference saved successfully' }));
    };

    /** Dispatch a DELETE of all UI preferences on the backend */
    clearPreference = (): void => {
        this.store$.dispatch(DeleteUiPreferences());
    };

    /**
     * Synchronously reads the landing page preference from the current store snapshot.
     * Returns undefined when no preference is set.
     */
    getPreference = (): LandingPagePreference | undefined => {
        let preference: LandingPagePreference | undefined;
        this.store$.select(getLandingPagePreference$).subscribe(pref => {
            preference = pref ?? undefined;
        }).unsubscribe();
        return preference;
    };

    /**
     * Resolve the landing page id configured for an organization, if any.
     * Returns undefined when the user has not configured a landing page for the
     * organization, in which case the application default behaviour applies.
     */
    getLandingPageId = (organizationId: string): string | undefined => {
        const preference = this.getPreference();
        if (!preference) {
            return undefined;
        }
        // Per-tenant override takes priority; fall back to global allTenantsPage
        return preference.tenantPages?.[organizationId] || preference.allTenantsPage || undefined;
    };

    /**
     * Route segments (relative to organizations/:organizationId) for the configured
     * landing page of an organization. Undefined when no preference applies.
     */
    getLandingSegments = (organizationId: string): string[] | undefined => {
        const pageId = this.getLandingPageId(organizationId);
        const option = pageId && LANDING_PAGE_OPTIONS.find(landingOption => landingOption.id === pageId);
        return option ? [...option.segments] : undefined;
    };
}
